import type { StatusTone } from '@repletoryreach/sdk-ui/data-display';
import type { Translate } from '../../../i18n/I18nProvider';

/**
 * Where the owner makes a display-name change for a number on the platform's
 * shared account. The console cannot rename a number itself: Meta only takes
 * the change in WhatsApp Manager, then reviews it.
 */
export const WHATSAPP_MANAGER_URL = 'https://business.facebook.com/wa/manage/phone-numbers/';

/** Meta's display-name review states this build has words for. */
const KNOWN_NAME_STATUSES = ['APPROVED', 'AVAILABLE_WITHOUT_REVIEW', 'PENDING_REVIEW', 'DECLINED', 'EXPIRED', 'NONE'] as const;
type KnownNameStatus = (typeof KNOWN_NAME_STATUSES)[number];

const NAME_TONES: Record<KnownNameStatus, StatusTone> = {
  APPROVED: 'healthy',
  AVAILABLE_WITHOUT_REVIEW: 'healthy',
  PENDING_REVIEW: 'pending',
  DECLINED: 'down',
  EXPIRED: 'warning',
  NONE: 'inactive'
};

function normaliseCode(value: string | null | undefined): string {
  return (value ?? '').trim().toUpperCase();
}

function isKnown(code: string): code is KnownNameStatus {
  return (KNOWN_NAME_STATUSES as readonly string[]).includes(code);
}

/**
 * A Meta code a person can read: `NAME_FORMAT_UNACCEPTABLE` becomes "Name
 * format unacceptable". Used for codes this build has no words for, so a new
 * one Meta adds still reads as words rather than a constant.
 */
export function readableCode(value: string | null | undefined): string {
  const words = (value ?? '').trim().replace(/[_\s]+/g, ' ').toLowerCase();
  return words ? words.charAt(0).toUpperCase() + words.slice(1) : '';
}

export function nameStatusTone(value: string | null | undefined): StatusTone {
  const code = normaliseCode(value);
  return isKnown(code) ? NAME_TONES[code] : 'inactive';
}

/** The review state in words; "Not checked" when Meta has never been asked. */
export function nameStatusLabel(t: Translate, value: string | null | undefined): string {
  const code = normaliseCode(value);
  if (!code) return t('gateways.numbers.nameStatus.unchecked');
  return isKnown(code) ? t(`gateways.numbers.nameStatus.${code}`) : readableCode(code);
}

/** A business asked for a new name and it has not been cleared yet. */
export function hasOpenNameRequest(row: { name_requested?: string | null }): boolean {
  return Boolean(row.name_requested?.trim());
}

export type PayerKey = 'platform' | 'customer' | 'unknown';

export function payerKey(value: string | null | undefined): PayerKey {
  return value === 'platform' || value === 'customer' ? value : 'unknown';
}

export function payerLabel(t: Translate, value: string | null | undefined): string {
  return t(`gateways.numbers.payer.${payerKey(value)}`);
}
